<template>
  <v-dialog
    v-model="show"
    max-width="620"
    scrollable
    transition="dialog-bottom-transition"
  >
    <v-card class="rc-card">
      <header class="rc-header">
        <div class="rc-header__icon">
          <v-icon color="white">mdi-message-plus-outline</v-icon>
        </div>
        <div class="rc-header__text">
          <span class="rc-title">Novo recado</span>
          <span class="rc-subtitle">{{ subtitle }}</span>
        </div>
        <v-spacer />
        <v-btn class="close-btn" icon dark aria-label="Fechar" @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <v-card-text class="ds-dialog-body">
        <div v-if="!targets.length" class="rc-empty">
          <v-icon size="44" class="rc-empty__icon">mdi-account-cancel-outline</v-icon>
          <p>Você não tem uma clínica com destinatário disponível para recado.</p>
        </div>

        <template v-else>
          <!-- Clínica: só pergunta quando há escolha real. -->
          <section v-if="clinicOptions.length > 1" class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-hospital-building</v-icon>
                <span>Clínica</span>
              </div>
            </div>
            <v-chip-group
              v-model="clinicId"
              mandatory
              column
              active-class="rc-chip--active"
              @change="onClinicChange"
            >
              <v-chip
                v-for="c in clinicOptions"
                :key="c.id"
                :value="c.id"
                small
                outlined
              >
                <v-icon x-small left>mdi-hospital-building</v-icon>
                {{ c.name }}
              </v-chip>
            </v-chip-group>
          </section>

          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-account</v-icon>
                <span>{{ recipientLabel }}</span>
              </div>
            </div>
            <div class="rc-recipients">
              <button
                v-for="r in recipientOptions"
                :key="r.id"
                type="button"
                class="rc-recipient"
                :class="{ 'rc-recipient--active': recipientId === r.id }"
                :aria-pressed="recipientId === r.id"
                @click="selectRecipient(r.id)"
              >
                <span class="rc-avatar" :style="{ background: avatarColor(r.name) }">
                  {{ initials(r.name) }}
                </span>
                <span class="rc-recipient__name">{{ r.name }}</span>
                <v-icon
                  small
                  class="rc-recipient__check"
                >mdi-check-circle</v-icon>
              </button>
            </div>
          </section>

          <section class="ds-form-section">
            <div class="ds-form-section__head">
              <div class="ds-form-section__title">
                <v-icon small color="primary" class="mr-2">mdi-message-text</v-icon>
                <span>Mensagem</span>
              </div>
            </div>
            <div class="rc-suggestions">
              <v-chip
                v-for="s in suggestions"
                :key="s"
                x-small
                class="rc-suggestion"
                @click="applySuggestion(s)"
              >
                <v-icon x-small left>mdi-lightning-bolt-outline</v-icon>
                {{ s }}
              </v-chip>
            </div>
            <v-textarea
              ref="message"
              v-model="message"
              :placeholder="messageLabel"
              outlined
              dense
              color="primary"
              rows="3"
              auto-grow
              counter="2000"
              maxlength="2000"
              class="rc-message"
              @keydown.enter.ctrl.prevent="send"
              @keydown.enter.meta.prevent="send"
            />
          </section>
        </template>
      </v-card-text>

      <v-card-actions v-if="targets.length" class="ds-dialog-actions">
        <span class="rc-hint">
          <v-icon x-small class="mr-1">mdi-keyboard-outline</v-icon>
          Ctrl + Enter envia
        </span>
        <v-spacer />
        <v-btn outlined color="primary" @click="show = false">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          depressed
          class="rc-send"
          :disabled="!canSend"
          :loading="sending"
          @click="send"
        >
          <v-icon left small>mdi-send</v-icon>
          Enviar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { createAssistantNote } from "@/services/assistantNoteService";
import { useSnackbar } from "@/composables/useSnackbar";

/** Tons da paleta para o avatar de iniciais — estáveis por nome. */
const AVATAR_COLORS = [
  "var(--ds-cat-teal)",
  "var(--ds-cat-indigo)",
  "var(--ds-cat-amber)",
  "var(--ds-cat-pink)",
  "var(--ds-cat-violet)",
  "var(--ds-cat-green)",
];

export default {
  name: "RecadoComposeDialog",

  props: {
    value: { type: Boolean, default: false },
    targets: { type: Array, default: () => [] },
    isAssistant: { type: Boolean, default: false },
  },

  setup() {
    const { error } = useSnackbar();
    return { notifyError: error };
  },

  data() {
    return { clinicId: null, recipientId: null, message: "", sending: false };
  },

  computed: {
    show: {
      get() { return this.value; },
      set(v) { this.$emit("input", v); },
    },
    clinicOptions() {
      return this.targets.map((t) => ({ id: t.clinic_id, name: t.clinic_name }));
    },
    recipientOptions() {
      const t = this.targets.find((x) => x.clinic_id === this.clinicId);
      return t ? t.recipients : [];
    },
    recipientLabel() {
      return this.isAssistant ? "Para qual médico" : "Para qual assistente";
    },
    subtitle() {
      return this.isAssistant
        ? "Deixe um aviso para o médico"
        : "Deixe uma tarefa ou aviso para a assistente";
    },
    messageLabel() {
      return this.isAssistant
        ? "Escreva um recado para o médico..."
        : "Escreva um recado para o assistente...";
    },
    /** Atalhos de texto: um clique preenche o começo mais comum do recado. */
    suggestions() {
      return this.isAssistant
        ? ["Paciente pediu retorno", "Reagendamento solicitado", "Documento recebido"]
        : ["Confirmar consultas de amanhã", "Ligar para o paciente", "Remarcar horário"];
    },
    canSend() {
      return !!this.clinicId && !!this.recipientId && !!this.message.trim();
    },
  },

  watch: {
    value(open) {
      if (open) this.reset();
    },
  },

  methods: {
    reset() {
      this.clinicId = this.targets.length ? this.targets[0].clinic_id : null;
      this.message = "";
      this.sending = false;
      this.autoSelectRecipient();
    },
    onClinicChange() {
      this.recipientId = null;
      this.autoSelectRecipient();
    },
    /** Destinatário único não é escolha: já vem marcado. */
    autoSelectRecipient() {
      this.recipientId =
        this.recipientOptions.length === 1 ? this.recipientOptions[0].id : null;
    },
    selectRecipient(id) {
      this.recipientId = id;
      this.$nextTick(() => this.$refs.message?.focus());
    },
    applySuggestion(text) {
      const atual = this.message.trim();
      this.message = atual ? `${atual}\n${text}: ` : `${text}: `;
      this.$nextTick(() => this.$refs.message?.focus());
    },
    initials(name) {
      const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return "?";
      const first = parts[0][0];
      const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase();
    },
    avatarColor(name) {
      let hash = 0;
      for (const ch of String(name || "")) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
      return AVATAR_COLORS[hash % AVATAR_COLORS.length];
    },
    async send() {
      if (!this.canSend || this.sending) return;
      this.sending = true;
      try {
        await createAssistantNote({
          clinic_id: this.clinicId,
          recipient_id: this.recipientId,
          message: this.message.trim(),
        });
        this.$emit("sent");
        this.show = false;
      } catch (e) {
        console.error("Erro ao enviar recado:", e);
        this.notifyError("Não foi possível enviar o recado.");
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.rc-card {
  border-radius: 14px !important;
  overflow: hidden;
  background: var(--ds-surface-white, #fff);
}

.rc-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 22px;
  color: #fff;
  background:
    radial-gradient(circle at 92% -20%, rgba(255, 255, 255, 0.22) 0%, transparent 45%),
    linear-gradient(135deg, var(--ds-primary) 0%, var(--ds-primary-deep) 100%);
}
.rc-header__icon {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.18);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.25);
}
.rc-header__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.rc-title {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
}
.rc-subtitle {
  font-size: 0.8rem;
  opacity: 0.85;
}

.rc-chip--active {
  color: var(--ds-primary-darker) !important;
  border-color: currentColor !important;
  background: var(--ds-primary-surface) !important;
}

.rc-recipients {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  padding: 2px;
}
.rc-recipient {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  text-align: left;
  color: var(--ds-text, #2c3e50);
  background: var(--ds-surface-white, #fff);
  border: 1px solid var(--ds-border-soft, #eef2f7);
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.14s ease, box-shadow 0.14s ease, border-color 0.14s ease,
    background 0.14s ease;
}
.rc-recipient:hover,
.rc-recipient:focus-visible {
  outline: none;
  transform: translateY(-1px);
  border-color: var(--ds-primary-border);
  box-shadow: 0 6px 16px rgba(31, 77, 63, 0.1);
}
.rc-recipient--active {
  border-color: var(--ds-primary);
  background: var(--ds-primary-surface);
  box-shadow: 0 0 0 1px var(--ds-primary);
}
.rc-avatar {
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.rc-recipient__name {
  flex: 1;
  min-width: 0;
  font-size: 0.85rem;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rc-recipient__check {
  color: var(--ds-primary) !important;
  opacity: 0;
  transform: scale(0.6);
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.rc-recipient--active .rc-recipient__check {
  opacity: 1;
  transform: scale(1);
}

.rc-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
}
.rc-suggestion {
  color: var(--ds-primary-darker) !important;
  background: var(--ds-primary-surface) !important;
  transition: transform 0.12s ease;
}
.rc-suggestion:hover {
  transform: translateY(-1px);
}
.rc-message :deep(textarea) {
  line-height: 1.5;
}

.rc-hint {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  color: var(--ds-text-soft, #78909c);
}
.rc-send {
  border-radius: var(--ds-radius-btn, 8px);
  padding: 0 18px !important;
}

.rc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 0;
  text-align: center;
  font-size: 0.88rem;
  color: var(--ds-text-soft, var(--ds-text-muted));
}
.rc-empty p {
  margin: 8px 0 0;
}
.rc-empty__icon {
  color: var(--ds-text-muted, #90a4ae) !important;
  opacity: 0.6;
}

@media (max-width: 600px) {
  .rc-hint {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .rc-recipient,
  .rc-recipient__check,
  .rc-suggestion {
    transition: none;
  }
}
</style>
